import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, TrendingDown, PieChart, FileText, Settings as SettingsIcon,
  Plus, Bell, LogOut, ChevronDown, Utensils,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, mobile: true },
  { to: "/revenue", label: "Revenue", icon: TrendingUp, mobile: true },
  { to: "/expenses", label: "Expenses", icon: TrendingDown, mobile: true },
  { to: "/allocation", label: "Profit Allocation", icon: PieChart, mobile: false },
  { to: "/reports", label: "Reports", icon: FileText, mobile: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, mobile: false },
];

const Brand = () => (
  <div className="flex items-center gap-3">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 text-white shadow-lg shadow-orange-600/30">
      <Utensils className="h-5 w-5" />
    </div>
    <div>
      <div className="font-heading text-lg font-extrabold leading-none text-white">SocietyBites</div>
      <div className="text-xs font-medium text-orange-400">Finance Portal</div>
    </div>
  </div>
);

export default function Layout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { openTransaction } = useApp();
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-64 flex-col bg-slate-900 lg:flex">
        <div className="border-b border-slate-800 p-6"><Brand /></div>
        <nav className="flex-1 space-y-1 p-4">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = isActive(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-orange-600 text-white shadow-lg shadow-orange-600/20" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <Icon className="h-5 w-5" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="space-y-4 p-4">
          <Button
            onClick={() => openTransaction()}
            data-testid="add-transaction-btn"
            className="w-full gap-2 bg-orange-600 py-6 text-base font-semibold hover:bg-orange-700"
          >
            <Plus className="h-5 w-5" /> Add Transaction
          </Button>
          <div className="rounded-xl border border-slate-800 bg-slate-800/40 p-3">
            <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Quick Add</div>
            <button onClick={() => openTransaction("revenue")} data-testid="quick-add-revenue" className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-sm text-slate-200 transition-colors hover:bg-slate-800">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white"><TrendingUp className="h-4 w-4" /></span>
              Add Revenue
            </button>
            <button onClick={() => openTransaction("expense")} data-testid="quick-add-expense" className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-sm text-slate-200 transition-colors hover:bg-slate-800">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-500 text-white"><TrendingDown className="h-4 w-4" /></span>
              Add Expense
            </button>
          </div>
          <div className="px-1 text-xs text-slate-500">© 2026 SocietyBites</div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur md:px-8">
          <div className="lg:hidden"><Brand /></div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <button className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100" data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-slate-100" data-testid="user-menu">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-600 text-sm font-bold text-white">
                    {(user?.name || "A")[0].toUpperCase()}
                  </span>
                  <div className="hidden text-left sm:block">
                    <div className="text-sm font-semibold leading-none text-slate-800">{user?.name || "Admin"}</div>
                    <div className="text-xs text-slate-400">Administrator</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs text-slate-500">{user?.email}</div>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 pb-24 md:p-8 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex h-16 items-center justify-around border-t border-slate-800 bg-slate-900 lg:hidden">
        {NAV.filter((n) => n.mobile).map((n) => {
          const Icon = n.icon;
          const active = isActive(n.to);
          return (
            <Link key={n.to} to={n.to} className={`flex flex-col items-center gap-1 text-xs ${active ? "text-orange-500" : "text-slate-400"}`}>
              <Icon className="h-5 w-5" />
              {n.label}
            </Link>
          );
        })}
      </nav>

      {/* Mobile FAB */}
      <button
        onClick={() => openTransaction()}
        data-testid="mobile-fab"
        className="fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-orange-600 text-white shadow-xl shadow-orange-600/40 transition-transform hover:scale-105 lg:hidden"
      >
        <Plus className="h-7 w-7" />
      </button>
    </div>
  );
}
